import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Smartphone, Info, CheckCircle, Clock } from 'lucide-react';
import { authAPI, memberAPI } from '../services/api';

interface ApkInfo {
  id: number;
  version: string;
  originalName: string;
  fileSize: number;
  uploadedAt: string;
  notes: string | null;
  downloadUrl: string;
  qrCode: string;
  alreadyDownloaded: boolean;
  downloadedAt: string | null;
}

interface DownloadHistory {
  apkId: number;
  version: string;
  notes: string | null;
  uploadedAt: string;
  downloadedAt: string;
}

export default function MemberAppDownload() {
  const navigate = useNavigate();
  const [apk, setApk] = useState<ApkInfo | null>(null);
  const [history, setHistory] = useState<DownloadHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [noApk, setNoApk] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  async function checkAuthAndLoad() {
    try {
      const authCheck = await authAPI.checkMemberAuth();
      if (!authCheck.authenticated) {
        navigate('/');
        return;
      }
      const result = await memberAPI.getLatestApk();
      if (result.exists && result.data) {
        setApk(result.data);
        setHistory(result.history || []);
      } else {
        setNoApk(true);
      }
    } catch (error) {
      console.error('Failed to load APK info:', error);
      setNoApk(true);
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload() {
    if (!apk) return;
    // Mark as downloaded
    try {
      await memberAPI.markApkDownloaded(apk.id);
      setApk({ ...apk, alreadyDownloaded: true, downloadedAt: new Date().toISOString() });
      // Add to history if not already there
      if (!history.find(h => h.apkId === apk.id)) {
        setHistory([{
          apkId: apk.id,
          version: apk.version,
          notes: apk.notes,
          uploadedAt: apk.uploadedAt,
          downloadedAt: new Date().toISOString(),
        }, ...history]);
      }
    } catch (e) {
      // Non-critical, continue with download
    }
    // Trigger download
    window.open(apk.downloadUrl, '_blank');
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/portal/dashboard')}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">戻る</span>
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">アプリダウンロード</h1>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {noApk ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <Smartphone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">現在、ダウンロード可能なアプリはありません</p>
            <p className="text-gray-400 text-sm mt-2">管理者がアプリをアップロードするまでお待ちください</p>
          </div>
        ) : apk && (
          <div className="space-y-6">
            {/* App Info Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* App Header with NEW badge or Downloaded status */}
              <div className={`p-6 text-white ${
                apk.alreadyDownloaded
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-700'
                  : 'bg-gradient-to-r from-green-500 to-emerald-600'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <Smartphone className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">SNS Launcher</h2>
                      <p className={apk.alreadyDownloaded ? 'text-blue-200' : 'text-green-100'}>
                        バージョン {apk.version}
                      </p>
                    </div>
                  </div>
                  {apk.alreadyDownloaded ? (
                    <div className="flex items-center gap-1 bg-white/20 px-3 py-1.5 rounded-full text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>済み</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 bg-yellow-400 text-yellow-900 px-3 py-1.5 rounded-full text-sm font-bold animate-pulse">
                      NEW
                    </div>
                  )}
                </div>
              </div>

              {/* QR Code Section */}
              <div className="p-6 flex flex-col items-center">
                <p className="text-gray-600 mb-4 text-center">
                  スマートフォンでQRコードをスキャンしてダウンロード
                </p>
                <div className="bg-white border-2 border-gray-100 rounded-xl p-4 shadow-sm">
                  <img
                    src={apk.qrCode}
                    alt="Download QR Code"
                    className="w-56 h-56 sm:w-64 sm:h-64"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  QRコードをスキャンするとAPKファイルが直接ダウンロードされます
                </p>
              </div>

              {/* Direct Download Button */}
              <div className="px-6 pb-6">
                <button
                  onClick={handleDownload}
                  className={`flex items-center justify-center gap-2 w-full py-3 rounded-lg font-semibold transition-colors text-lg ${
                    apk.alreadyDownloaded
                      ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200'
                      : 'bg-green-500 hover:bg-green-600 text-white'
                  }`}
                >
                  <Download className="w-5 h-5" />
                  {apk.alreadyDownloaded
                    ? `再ダウンロード (${formatFileSize(apk.fileSize)})`
                    : `APKをダウンロード (${formatFileSize(apk.fileSize)})`
                  }
                </button>
              </div>
            </div>

            {/* App Details */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                アプリ情報
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">バージョン</span>
                  <span className="font-medium text-gray-800">{apk.version}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">ファイルサイズ</span>
                  <span className="font-medium text-gray-800">{formatFileSize(apk.fileSize)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">更新日</span>
                  <span className="font-medium text-gray-800">{formatDate(apk.uploadedAt)}</span>
                </div>
                {apk.downloadedAt && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">ダウンロード日</span>
                    <span className="font-medium text-green-600">{formatDate(apk.downloadedAt)}</span>
                  </div>
                )}
                {apk.notes && (
                  <div className="py-2">
                    <span className="text-gray-500 block mb-1">更新内容</span>
                    <p className="text-gray-800 whitespace-pre-wrap">{apk.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Download History */}
            {history.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-500" />
                  ダウンロード履歴
                </h3>
                <div className="space-y-2">
                  {history.map((item) => (
                    <div key={item.apkId} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <div>
                          <span className="text-sm font-medium text-gray-700">{item.version}</span>
                          {item.notes && (
                            <span className="text-xs text-gray-400 ml-2">{item.notes}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(item.downloadedAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Install Instructions */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="font-bold text-blue-800 mb-3">インストール手順</h3>
              <ol className="space-y-2 text-sm text-blue-700">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  <span>上のQRコードをスキャンするか、ダウンロードボタンをタップ</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  <span>ダウンロード完了後、APKファイルを開く</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  <span>「不明なアプリのインストール」を許可して、インストール</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  <span>インストール完了後、アプリを起動</span>
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
